import React, { useState } from 'react';
import {
  EmptySearch,
  FullScreenModalContent,
  FullScreenModalFooter,
  PreviewModelHeader,
  VehicleMake,
  VehicleMakeHeader,
  VehicleModel,
  VehicleModelHeader,
  VehicleYear,
  VehicleYearHeader,
  VideoRow,
  VideosList,
} from '../style';
import { Gap } from 'lib/components/styles/layout';
import { useGetVINReelsQuery } from 'lib/api/VINReels/useVINReelsQuery';
import { debounce } from 'lodash';
import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
  PaginationConstants,
} from 'lib/const/PaginationConstants';
import { LoadingIndicator } from 'lib/components/LoadingIndicator';
import { ParagraphSmallBold } from 'lib/components/styles/typography';
import {
  ModalVideoListVideo,
  Search,
  TableContextProvider,
  TablePaginationNew,
} from 'lib/components';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import { VINReel, VINReelSortKey } from 'lib/api/VINReels/types';
import { VideoItemThumbnail } from 'app/pages/library/videos/components';
import { Button } from 'react-covideo-common';
import imagePlaceholder from 'assets/images/single/imagePlaceholder.webp';
import { useTheme } from 'styled-components/macro';
import { useCopyVINReelMutation } from 'lib/api/VINReels/useCopyVINReelMutation';
import { getDisplayName } from 'lib/utils/functions';

type Props = {
  handleModalClose?: () => void;
  onCopy?: () => void;
  showAddToCovideo?: boolean;
  onSelectVideo?: (data: VINReel) => void;
};

export const VINReelsTable = ({
  showAddToCovideo = true,
  onSelectVideo,
  handleModalClose,
  onCopy,
}: Props) => {
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [vin, setVin] = useState('');
  const [order, setOrder] = useState<PaginationConstants>(
    PaginationConstants.ASCENDING
  );
  const [sortKey, setSortKey] = useState<VINReelSortKey | null>(null);
  const onSuccessCopy = () => {
    onCopy?.();
    handleModalClose?.();
  };

  const { data, isFetching } = useGetVINReelsQuery({
    start: page * DEFAULT_SIZE,
    limit: DEFAULT_SIZE,
    search: searchTerm,
    ...(!!sortKey && !!order ? { sortKey, order } : {}),
  });

  const { mutateAsync: copyVINReel, isLoading: isCopying } =
    useCopyVINReelMutation({ onSuccessCallback: onSuccessCopy });

  const themes = useTheme();
  const isLoading = isFetching || isCopying;
  const showSelectVideo = !!onSelectVideo;

  const selectedVINReel = (data?.data || []).find(
    vinReel => vinReel.vin === vin
  );

  const onPaginationChangeHandler = debounce(
    async ({ page: newPage }: { page: number }) => {
      if (page !== newPage) {
        setPage(newPage);
      }
    },
    300
  );

  const getEmptyStateMessage = () => {
    if (searchTerm) {
      return {
        primary: `There are no videos with "${searchTerm}" in the title.`,
        secondary: 'Try using a different search term.',
      };
    }
    return {
      primary:
        "Unfortunately, there aren't any videos available at the moment.",
      secondary: '',
    };
  };

  const onSearch = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleSortChange = (
    sortKey: VINReelSortKey,
    order: PaginationConstants
  ) => {
    setOrder(order);
    setSortKey(sortKey);
    setPage(DEFAULT_PAGE);
  };

  const copyVINReelHandler = async (vinReel: VINReel) => {
    const inventoryItem = vinReel?.inventoryItem;
    if (!inventoryItem) {
      return;
    }
    const videoTitle = getDisplayName([
      inventoryItem?.year.toString(),
      inventoryItem.make,
      inventoryItem.model,
      inventoryItem.stockNumber,
    ]);

    await copyVINReel({
      vin: vinReel.vin,
      videoTitle,
    });
  };

  return (
    <FullScreenModalContent>
      <Gap
        m='0 0 20px auto'
        alignItems='flex-end'
        justifyContent='space-between'
        width='100%'
      >
        <Search
          placeholder='Search videos...'
          onSearch={onSearch}
          width='300px'
          disabled={isLoading}
        />
      </Gap>

      {isLoading ? (
        <div>
          <LoadingIndicator isLoading />
        </div>
      ) : !data?.data.length ? (
        <EmptySearch>
          {(() => {
            const message = getEmptyStateMessage();
            return (
              <>
                <ParagraphSmallBold color={themes.colors.secondary[60]}>
                  {message.primary}
                </ParagraphSmallBold>
                {message.secondary && (
                  <ParagraphSmallBold color={themes.colors.secondary[60]}>
                    {message.secondary}
                  </ParagraphSmallBold>
                )}
              </>
            );
          })()}
        </EmptySearch>
      ) : (
        <>
          <VideosList>
            <VideoRow>
              <PreviewModelHeader>Preview</PreviewModelHeader>
              <VehicleYearHeader>
                Year
                <SortButtons
                  isSorted={sortKey === VINReelSortKey.YEAR}
                  order={order}
                  onChange={value =>
                    handleSortChange(VINReelSortKey.YEAR, value)
                  }
                />
              </VehicleYearHeader>
              <VehicleMakeHeader>
                Make
                <SortButtons
                  isSorted={sortKey === VINReelSortKey.MAKE}
                  order={order}
                  onChange={value =>
                    handleSortChange(VINReelSortKey.MAKE, value)
                  }
                />
              </VehicleMakeHeader>
              <VehicleModelHeader>
                Model
                <SortButtons
                  isSorted={sortKey === VINReelSortKey.MODEL}
                  order={order}
                  onChange={value =>
                    handleSortChange(VINReelSortKey.MODEL, value)
                  }
                />
              </VehicleModelHeader>
            </VideoRow>
            {(data?.data || [])?.map((vinReel: VINReel) => (
              <VideoRow key={vinReel.vin}>
                <VideoItemThumbnail
                  handleClick={() => setVin(vinReel.vin)}
                  thumbnail={vinReel.thumbnailUrl || imagePlaceholder}
                  videoId={vinReel.vin}
                  processing={0}
                  thumbnailHeight={'80%'}
                  thumbnailWidth={'15%'}
                />
                <VehicleYear>{vinReel?.inventoryItem?.year || ''}</VehicleYear>
                <VehicleMake>{vinReel?.inventoryItem?.make || ''}</VehicleMake>
                <VehicleModel>
                  {vinReel?.inventoryItem?.model || ''}
                </VehicleModel>
                <Gap flexWrap='noWrap' gap='10px'>
                  {showAddToCovideo && (
                    <Button
                      variant='secondary'
                      text='Add to Covideo'
                      onClick={() => copyVINReelHandler(vinReel)}
                    />
                  )}
                  {showSelectVideo && (
                    <Button
                      variant='secondary'
                      text='Select Video'
                      onClick={() => onSelectVideo(vinReel)}
                    />
                  )}
                </Gap>
              </VideoRow>
            ))}
          </VideosList>
          <FullScreenModalFooter>
            <TableContextProvider
              total={data?.count || 0}
              initSize={DEFAULT_SIZE}
              onChange={onPaginationChangeHandler}
              initPage={page}
            >
              <TablePaginationNew disabled={isLoading} />
            </TableContextProvider>
          </FullScreenModalFooter>

          {!!vin && !!selectedVINReel && !!selectedVINReel?.videoUrl && (
            <ModalVideoListVideo
              videoId={''}
              source={selectedVINReel?.videoUrl || ''}
              handleModalClose={() => {
                setVin('');
              }}
              hideContext={true}
            />
          )}
        </>
      )}
    </FullScreenModalContent>
  );
};
